import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/ContactUs.css';

function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState(null);
  const [errors, setErrors] = useState({});

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) newErrors.email = 'Invalid email format';
    if (!formData.mobile.match(/^\+?[\d\s-]{10,}$/)) newErrors.mobile = 'Mobile number must be at least 10 digits';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact-us`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit contact form');
      }

      setFormStatus('success');
      setFormData({ name: '', email: '', mobile: '', message: '' });
      setErrors({});
      setTimeout(() => setFormStatus(null), 5000);
    } catch (error) {
      console.error('Error:', error);
      setFormStatus('error');
      setErrors({ general: error.message || 'An error occurred. Please try again.' });
      setTimeout(() => setFormStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.5, ease: 'easeOut' }
    })
  };

  const inputVariants = {
    initial: { scale: 1, boxShadow: '0 0 0 rgba(0,0,0,0)' },
    focus: { 
      scale: 1.02, 
      boxShadow: '0 0 8px rgba(180, 83, 9, 0.3)',
      transition: { duration: 0.3 }
    }
  };

  const buttonVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.05, backgroundColor: '#92400E' },
    tap: { scale: 0.95 }
  };

  return (
    <main className="contact-us-main">
      {/* Hero Section */}
      <motion.section
        className="contact-hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="contact-hero-content">
          <motion.h1
            className="contact-hero-title"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Get in Touch
          </motion.h1>
          <motion.p
            className="contact-hero-subtitle"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            We'd love to hear from you! Reach out with any questions or to book your next cycling adventure.
          </motion.p>
        </div>
      </motion.section>

      {/* Contact Info Section */}
      <motion.section
        className="contact-info-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={sectionVariants}
      >
        <div className="section-container">
          <h2 className="section-title">Contact Information</h2>
          <div className="contact-info-grid">
            {[
              { icon: '📞', title: 'Phone', content: <a href="tel:+919894150416">+91 98941 50416</a> },
              { icon: '✉️', title: 'Email', content: <a href="mailto:info@beavertours.com">beavertours786@gmail.com</a> },
              { 
                icon: <img src="/assets/images/insta.png" alt="Instagram Logo" className="contact-info-instagram-icon" />, 
                title: 'Instagram', 
                content: <a href="https://www.instagram.com/beavertours/" target="_blank" rel="noopener noreferrer">@beavertours</a> 
              },
              { 
                icon: <img src="/assets/images/whatsapp.png" alt="WhatsApp Logo" className="contact-info-social-icon" />, 
                title: 'WhatsApp', 
                content: <a href="https://wa.me/919894150416" target="_blank" rel="noopener noreferrer">+91 98941 50416</a> 
              },
              { 
                icon: '📍', 
                title: 'Address', 
                content: '4-A, Poosaripalayam, Balaji Nagar, Coimbatore, Tamil Nadu 641006.'
              },
              { 
                icon: '🗺️', 
                title: 'Track Vehicles', 
                content: <a href="https://www.protrack365.com/" target="_blank" rel="noopener noreferrer">Track you Vehicles</a> 
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="contact-info-card"
                custom={index}
                variants={cardVariants}
                whileHover={{ scale: 1.05, boxShadow: '0 8px 20px rgba(0,0,0,0.1)' }}
              >
                <motion.div
                  className="contact-info-icon"
                  whileHover={{ rotate: 10, scale: 1.2 }}
                  transition={{ duration: 0.3 }}
                >
                  {item.icon}
                </motion.div>
                <h3>{item.title}</h3>
                <p>{item.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Map Section */}
      <motion.section
        className="map-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={sectionVariants}
      >
        <div className="section-container">
          <h2 className="section-title">Find Us</h2>
          <motion.div
            className="map-container"
            variants={cardVariants}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3916.203034721237!2d76.97686147480864!3d11.023388089140784!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba8590021830f15%3A0xc695c6443ed7b1b4!2sBEAVER%20TOURS%20Bike%20rentals!5e0!3m2!1sen!2sin!4v1749481763601!5m2!1sen!2sin"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="Beaver Tours Location"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            ></motion.iframe>
          </motion.div>
        </div>
      </motion.section>

      {/* Contact Form Section */}
      <motion.section
        className="contact-form-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={sectionVariants}
      >
        <div className="section-container">
          <h2 className="section-title">Send Us a Message</h2>
          <motion.form
            className="contact-form"
            onSubmit={handleSubmit}
            noValidate
            aria-live="polite"
          >
            {[
              { name: 'name', label: 'Name', type: 'text' },
              { name: 'email', label: 'Email', type: 'email' },
              { name: 'mobile', label: 'Mobile Number', type: 'tel' },
              { name: 'message', label: 'Message', type: 'textarea' }
            ].map((field, index) => (
              <motion.div
                key={field.name}
                className="form-group"
                variants={cardVariants}
                custom={index}
              >
                <label htmlFor={field.name}>{field.label}</label>
                {field.type === 'textarea' ? (
                  <motion.textarea
                    id={field.name}
                    name={field.name}
                    placeholder={`Your ${field.label}`}
                    value={formData[field.name]}
                    onChange={handleChange}
                    required
                    variants={inputVariants}
                    initial="initial"
                    whileFocus="focus"
                    aria-describedby={errors[field.name] ? `${field.name}-error` : undefined}
                  />
                ) : (
                  <motion.input
                    type={field.type}
                    id={field.name}
                    name={field.name}
                    placeholder={`Your ${field.label}`}
                    value={formData[field.name]}
                    onChange={handleChange}
                    required
                    variants={inputVariants}
                    initial="initial"
                    whileFocus="focus"
                    aria-describedby={errors[field.name] ? `${field.name}-error` : undefined}
                  />
                )}
                {errors[field.name] && (
                  <p className="form-error" id={`${field.name}-error`}>
                    {errors[field.name]}
                  </p>
                )}
              </motion.div>
            ))}
            {errors.general && (
              <p className="form-error form-error-general">{errors.general}</p>
            )}
            <motion.button
              type="submit"
              className="form-submit-button"
              variants={buttonVariants}
              initial="initial"
              whileHover="hover"
              whileTap="tap"
              disabled={isSubmitting}
              aria-disabled={isSubmitting}
            >
              {isSubmitting ? (
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  ⏳
                </motion.span>
              ) : (
                'Send Message'
              )}
            </motion.button>
            <AnimatePresence>
              {formStatus && (
                <motion.p
                  className={`form-status ${formStatus}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  role="alert"
                >
                  {formStatus === 'success'
                    ? 'Message sent successfully!'
                    : 'An error occurred. Please try again.'}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.form>
        </div>
      </motion.section>
    </main>
  );
}

export default ContactUs;